import type { InferOutput } from 'valibot'
import { isValidOrganizationNumber, orgNoSchema, withoutPrefix } from '#shared/organization-number'
import defu from 'defu'
import { ofetch } from 'ofetch'
import { withQuery } from 'ufo'
import { array, is, nullish, number, object, parse, string } from 'valibot'

const brregLinkSchema = object({ href: string() })

const brregResponseSchema = object({
  _links: object({
    next: nullish(brregLinkSchema),
    last: nullish(brregLinkSchema),
    prev: nullish(brregLinkSchema),
    self: brregLinkSchema,
    first: nullish(brregLinkSchema),
  }),
  _embedded: nullish(object({
    enheter: array(brregEnhetSchema),
  })),
  page: object({
    number: number(),
    size: number(),
    totalPages: number(),
    totalElements: number(),
  }),
})

function emptyResponse(selfHref: string): BrregResponse {
  return {
    _links: {
      self: {
        href: selfHref,
      },
    },
    page: {
      number: 0,
      size: 0,
      totalElements: 0,
      totalPages: 0,
    },
  }
}

type BrregResponse = InferOutput<typeof brregResponseSchema>

function useBrregQueryOptions(query: MaybeRef<string | undefined | null>, selectedOrganization?: MaybeRefOrGetter<BrregEnhet | undefined>) {
  const initialPageParam = computed(() => {
    const q = unref(query)

    return withQuery(BRREG_BASE_URL, {
      [is(orgNoSchema, q) ? 'organisasjonsnummer' : 'navn']: q,
      underAvvikling: false,
      underTvangsavviklingEllerTvangsopplosning: false,
      underKonkursbehandling: false,
      konkurs: false,
      size: 30,
      organisasjonsform: brregOrganisasjonsformSchema.options.filter(it => it !== 'UTLA').join(','),
    })
  })

  const queryClient = useQueryClient()

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryClient is a stable Vue Query injection, not a real dep
  return infiniteQueryOptions({
    initialPageParam,
    queryKey: ['brreg', query],
    staleTime: 1000 * 60 * 60 * 24,
    enabled: computed(() => {
      const q = toValue(query)
      return !!q?.trim() && q !== toValue(selectedOrganization)?.navn
    }),
    async queryFn({ pageParam, signal }) {
      if (!toValue(query)?.trim()) {
        // This should have been checked by the enabled property, but for some reason the queryFn still gets called on empty queries, so we return an empty response early so we don't touch the API.
        return emptyResponse(pageParam)
      }

      // Uses plain `ofetch` (not Nuxt's typed `$fetch`) since this is a fully external API, never part of this app's own routes.
      const data = await ofetch(pageParam, { signal })

      const parsed = parse(brregResponseSchema, data)

      const units = parsed._embedded?.enheter ?? []

      for (const unit of units) {
        if (isValidOrganizationNumber(unit.organisasjonsnummer)) {
          queryClient.setQueryData(
            ['brreg', 'unit', withoutPrefix(unit.organisasjonsnummer)],
            unit,
          )
        }
      }

      return parsed
    },
    getNextPageParam: lastPage => lastPage._links.next?.href,
    getPreviousPageParam: firstPage => firstPage._links.prev?.href,
  })
}

type BrregQueryOptions = ReturnType<typeof useBrregQueryOptions>

export function useBrregQuery(query: MaybeRef<string | undefined | null>, selectedOrganization?: MaybeRefOrGetter<BrregEnhet | undefined>, options?: Partial<BrregQueryOptions>) {
  const baseOptions = useBrregQueryOptions(query, selectedOrganization)

  return useInfiniteQuery(defu<BrregQueryOptions, [BrregQueryOptions]>(options, baseOptions))
}
